const XLSX = require('@sheet/core')
const JSZip = require('@sheet/core/jszip')
const { DEFAULT_COLUMNS, getRows, getColumnTitle } = require('../tools')

const SHEET_NAME = 'products'
const DESCRIPTION_ROW_COUNT = 1

const HEADER_ROW_HEIGHT = 60
const DESCRIPTION_ROW_HEIGHT = 100
const MIN_COL_WIDTH = 16
const MAX_COL_WIDTH = 32

const GRID_BORDER = { style: 'thin', color: { rgb: 'B0B0B0' } }

const HEADER_STYLE = {
  bold: true,
  color: { rgb: 'FFFFFF' },
  fgColor: { rgb: '1F4E79' },
  alignment: { wrapText: true, vertical: 'center' },
  top: GRID_BORDER,
  right: GRID_BORDER,
  bottom: GRID_BORDER,
  left: GRID_BORDER
}

const DESCRIPTION_STYLE = {
  italic: true,
  color: { rgb: '374151' },
  fgColor: { rgb: 'EFF6FF' },
  alignment: { wrapText: true, vertical: 'center' },
  top: GRID_BORDER,
  right: GRID_BORDER,
  bottom: GRID_BORDER,
  left: GRID_BORDER
}

const DATA_STYLE_GRAY = {
  fgColor: { rgb: 'f7f7f9' },
  top: GRID_BORDER,
  right: GRID_BORDER,
  bottom: GRID_BORDER,
  left: GRID_BORDER
}

const SERVICE_COLUMN = /^programs\..+\.services\./
const SERVICE_GROUP_SIZE = 3
const FIRST_DATA_ROW = 3

const isGrayServiceGroup = serviceColumnIndex =>
  Math.floor(serviceColumnIndex / SERVICE_GROUP_SIZE) % 2 === 0

const columnWidth = (title, description) => {
  const titleWidth = (title || '').length
  const descriptionWidth = description ? Math.min(description.length, MAX_COL_WIDTH) : 0
  return {
    wch: Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.max(titleWidth, descriptionWidth) + 2))
  }
}

const styleExistingRow = (ws, rowNumber, columnCount, style) => {
  for (let c = 0; c < columnCount; c++) {
    const ref = XLSX.utils.encode_col(c) + rowNumber
    if (!ws[ref]) ws[ref] = { t: 's', v: '' }
    ws[ref].s = style
  }
}

const styleGrayServiceDataColumns = (ws, headers, productRowCount) => {
  if (productRowCount === 0) return
  const lastDataRow = 2 + productRowCount
  let serviceColumnIndex = 0
  for (let c = 0; c < headers.length; c++) {
    if (!SERVICE_COLUMN.test(headers[c])) continue
    const gray = isGrayServiceGroup(serviceColumnIndex)
    serviceColumnIndex++
    if (!gray) continue

    const col = XLSX.utils.encode_col(c)
    for (let r = FIRST_DATA_ROW; r <= lastDataRow; r++) {
      const ref = col + r
      if (!ws[ref]) ws[ref] = { t: 's', v: '' }
      ws[ref].s = DATA_STYLE_GRAY
    }
  }
}

const withAppliedBorders = arrayBuffer => {
  const zip = new JSZip(arrayBuffer)
  const stylesFile = zip.file('xl/styles.xml')
  if (!stylesFile) return arrayBuffer
  const patched = stylesFile.asText().replace(/<xf\b([^>]*?)\s*\/>/g, (match, attrs) => {
    if (!/borderId="[1-9]/.test(attrs) || /applyBorder=/.test(attrs)) return match
    return `<xf${attrs} applyBorder="1" applyFill="1"/>`
  })
  zip.file('xl/styles.xml', patched)
  return zip.generate({ type: 'uint8array', compression: 'DEFLATE' })
}

const encode = (products, opts = {}) => {
  const { rows, headers, descriptions } = getRows(products, opts)
  const ws = XLSX.utils.aoa_to_sheet([
    headers,
    descriptions,
    ...rows
  ])

  styleExistingRow(ws, 1, headers.length, HEADER_STYLE)
  styleExistingRow(ws, 2, headers.length, DESCRIPTION_STYLE)
  styleGrayServiceDataColumns(ws, headers, rows.length)
  ws['!rows'] = [
    { hpx: HEADER_ROW_HEIGHT },
    { hpx: DESCRIPTION_ROW_HEIGHT }
  ]
  ws['!cols'] = headers.map((title, index) => columnWidth(title, descriptions[index]))
  // LMD freeze: cell ref with a column split. A3 (rows only) is ignored by Excel.
  ws['!freeze'] = 'B3'

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME)
  return withAppliedBorders(XLSX.write(wb, {type: 'array', compression: true, cellStyles: true}))
}

const decode = buffer => {
  if (!buffer) {
    throw new Error('buffer parameter is required')
  }

  const wb = XLSX.read(buffer, {type: 'array'})
  const ws = wb.Sheets[SHEET_NAME]

  if (!ws) {
    throw new Error(`Worksheet '${SHEET_NAME}' not found`)
  }

  return XLSX.utils.sheet_to_json(ws).slice(DESCRIPTION_ROW_COUNT)
}

// convert sheet row to actual product doc based on the column titles
const sheetToDoc = (sheetProduct, additionalColumns = {}) => {
  const columns = Object.assign({}, DEFAULT_COLUMNS, additionalColumns)
  return Object.keys(columns).reduce((doc, key) => {
    const title = getColumnTitle(columns[key])
    // filter of null or undefined inputs
    if (sheetProduct[title]) {
      doc[key] = sheetProduct[title]
    }
    return doc
  }, {})
}

module.exports = {
  encode, decode, SHEET_NAME, sheetToDoc
}
